import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getFinancialDashboard } from 'services/fund'

const toman = (value: number) =>
    `${Number(value || 0).toLocaleString('fa-IR')} تومان`

// Android @drawable/info tinted colorPrimary (#74B743): filled green
// circle with a white 'i' — the same guide mark used across the redesign.
function InfoMark() {
    return (
        <svg
            className=" shrink-0"
            width="15"
            height="15"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle cx="25" cy="25" r="23" fill="#74B743" />
            <path
                d="M25,11A3,3 0,0 0,22 14A3,3 0,0 0,25 17A3,3 0,0 0,28 14A3,3 0,0 0,25 11zM21,21L21,23L22,23L23,23L23,36L22,36L21,36L21,38L22,38L23,38L27,38L28,38L29,38L29,36L28,36L27,36L27,21L26,21L22,21L21,21z"
                fill="#fff"
            />
        </svg>
    )
}

function Row({
    label,
    value,
    guide,
    openGuide,
    setOpenGuide,
    id,
    strong,
}: {
    label: string
    value: number
    guide?: string
    openGuide?: string | null
    setOpenGuide?: (v: string | null) => void
    id?: string
    strong?: boolean
}) {
    const isOpen = !!guide && openGuide === id
    return (
        <div className=" relative flex items-center justify-between py-2.5 border-b border-divider last:border-0">
            <button
                type="button"
                className=" flex items-center gap-1.5 text-right"
                onClick={() =>
                    guide && setOpenGuide && setOpenGuide(isOpen ? null : id!)
                }
            >
                {guide && <InfoMark />}
                <span className=" text-sm text-text-secondary">{label}</span>
            </button>
            <span
                className={` text-sm font-bold ${
                    strong ? 'text-brand-deep' : 'text-text-body'
                }`}
            >
                {toman(value)}
            </span>
            {isOpen && (
                <div
                    className=" absolute top-full right-0 z-30 mt-1.5 w-max max-w-[280px] rounded-[8px] bg-green-1 px-3 py-2 text-right text-[13px] leading-6 text-white shadow-pop"
                    onClick={() => setOpenGuide && setOpenGuide(null)}
                >
                    {guide}
                </div>
            )}
        </div>
    )
}

function SectionCard({
    title,
    accent,
    children,
}: {
    title: string
    accent: 'income' | 'expense' | 'neutral'
    children: React.ReactNode
}) {
    const dot =
        accent === 'income'
            ? 'bg-success-1'
            : accent === 'expense'
            ? 'bg-danger'
            : 'bg-brand'
    const titleColor =
        accent === 'income'
            ? 'text-success-on'
            : accent === 'expense'
            ? 'text-danger-on'
            : 'text-brand-deep'
    return (
        <div className=" bg-white rounded-2xl border border-stroke shadow-card p-4 mt-3">
            <div className=" flex items-center gap-2 mb-1">
                <span className={` h-2 w-2 rounded-full ${dot}`} />
                <p className={` text-sm font-bold ${titleColor}`}>{title}</p>
            </div>
            {children}
        </div>
    )
}

export default function FinancialDashboard() {
    const { id } = useParams()
    const [openGuide, setOpenGuide] = useState<string | null>(null)
    const { data, isLoading } = useQuery(
        ['financial-dashboard', id],
        getFinancialDashboard
    )

    // The healthy state is تراز (≈0): loan کارمزد + هبهٔ هزینه should be fully
    // spent on box costs. مازاد = کارمزد still un-spent (شرعاً must go to costs,
    // not lending) → amber. کسری = costs ate into members' capital → red.
    const tone: {
        [key: string]: {
            label: string
            bg: string
            num: string
            chip: string
            explain: string
        }
    } = {
        balanced: {
            label: 'تراز',
            bg: 'bg-brand-soft',
            num: 'text-brand-deep',
            chip: 'bg-brand text-white',
            explain: 'هزینه‌های صندوق و درآمدِ مجاز برای پوشش آن‌ها تراز است.',
        },
        surplus: {
            label: 'مازاد',
            bg: 'bg-warning-soft',
            num: 'text-warning-on',
            chip: 'bg-warning-1 text-white',
            explain:
                'بخشی از کارمزد وام‌ها هنوز صرف هزینه‌های صندوق نشده است؛ طبق حکم شرعی این مبلغ باید صرف هزینه‌های صندوق شود، نه وام‌دهی.',
        },
        deficit: {
            label: 'کسری',
            bg: 'bg-danger-soft',
            num: 'text-danger-on',
            chip: 'bg-danger text-white',
            explain:
                'هزینه‌های صندوق بیش از درآمدِ مجاز برای پوشش آن بوده و بخشی از سرمایهٔ اعضا صرف هزینه‌ها شده است.',
        },
    }
    const t = (data && tone[data.status]) || tone.balanced

    return (
        <div className=" bg-canvas min-h-screen">
            <Header title="داشبورد هزینه و درآمد صندوق" />

            {isLoading && <DotsLoading />}

            {!isLoading && data && (
                <div className=" px-4 pt-4 pb-8">
                    {data.ledger_warning && (
                        <div className=" bg-warning-soft border border-warning-1/40 text-warning-on rounded-xl p-3 text-xs text-center leading-6 mb-3">
                            احتمال مغایرت در دفترکل این صندوق وجود دارد؛ اعداد
                            زیر را با احتیاط بررسی کنید.
                        </div>
                    )}

                    {/* Headline: cost-coverage balance — healthy target is تراز (≈0) */}
                    <div
                        className={` rounded-2xl shadow-card p-5 text-center ${t.bg}`}
                    >
                        <p className=" text-sm text-text-secondary">
                            تراز هزینه و درآمد صندوق
                        </p>
                        <p
                            className={` text-[26px] font-extrabold mt-1.5 ${t.num}`}
                        >
                            {toman(data.cost_coverage_balance)}
                        </p>
                        <span
                            className={` inline-block mt-2 rounded-full px-4 py-0.5 text-xs font-bold ${t.chip}`}
                        >
                            {t.label}
                        </span>
                        <p className=" text-[11px] text-text-secondary mt-2 leading-6">
                            این عدد در حالت سالم باید تراز (نزدیک صفر) باشد؛ یعنی
                            کارمزد وام‌ها و هبهٔ هزینه دقیقاً صرف هزینه‌های صندوق شود.
                        </p>
                        <p className={` text-xs mt-2 leading-6 ${t.num}`}>
                            {t.explain}
                        </p>
                    </div>

                    {/* Income */}
                    <SectionCard title="درآمدهای صندوق" accent="income">
                        <Row
                            id="accrued"
                            label="سود مصوب وام‌ها (کارمزد)"
                            value={data.income.loan_interest_accrued}
                            guide="سود کل وام‌های فعال طبق جدول اقساط محاسبه می‌شود؛ همهٔ آن هنوز وصول نشده است."
                            openGuide={openGuide}
                            setOpenGuide={setOpenGuide}
                        />
                        <p className=" text-[11px] text-text-secondary -mt-1 mb-1 leading-6">
                            {data.income.loan_interest_realized >=
                            data.income.loan_interest_accrued
                                ? 'کل این مبلغ تا کنون وصول شده است.'
                                : `از این مبلغ، ${toman(
                                      data.income.loan_interest_realized
                                  )} تا کنون وصول شده و باقیِ آن هنوز وصول نشده است.`}
                        </p>
                        <Row
                            label="هبه برای پوشش هزینه"
                            value={data.income.hibah_expense}
                        />
                    </SectionCard>

                    {/* Expenses */}
                    <SectionCard title="هزینه‌های صندوق" accent="expense">
                        <Row
                            label="برداشت بابت هزینه‌های صندوق"
                            value={data.expenses.box_cost_cashouts}
                        />
                    </SectionCard>

                    {/* Sharia note */}
                    <div className=" bg-brand-soft rounded-2xl p-3.5 mt-3 flex items-start gap-2">
                        <InfoMark />
                        <p className=" text-xs text-brand-deep leading-6">
                            {data.sharia_note}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
